import { useGlobalSearchParams } from "expo-router";

import { useGetAllConversations } from "@/api/queries/conversation";
import { useFeatureFlag } from "@/components/FeatureFlagProvider";
import { useCurrentConversationId } from "@/hooks/useCurrentConversationId";
import { AssistantMessageTypes } from "@/types";
import { useGroupMessages, useKeepConversationsOpen } from "./useGroupMessages";
import { useInfiniteMessages } from "./useInfiniteMessages";

export { useCurrentConversationId } from "./useCurrentConversationId";

export const useCurrentNotificationId = () => {
  const { notificationId } = useGlobalSearchParams<{
    notificationId?: string;
  }>();
  return notificationId;
};

export const useCurrentConversationType = () => {
  const conversationId = useCurrentConversationId();
  const { data, isPending, isError } = useInfiniteMessages(conversationId);
  return {
    data: data?.pages[0]?.conversation?.type,
    isPending,
    isError,
  };
};

/**
 * The group the open conversation belongs to, which is how a history row knows it is the
 * selected one. Read off the messages query rather than the conversation list, because that
 * list only holds its first page and the open conversation may be older than that.
 *
 * `enabled` exists because subscribing to the messages query is not free — it seeds the
 * user-sent-event store and polls — so a caller that will not use the answer passes false.
 */
export const useCurrentConversationGroupId = ({
  enabled = true,
}: {
  enabled?: boolean;
} = {}) => {
  const conversationId = useCurrentConversationId();
  const { data } = useInfiniteMessages(enabled ? conversationId : undefined);
  return data?.pages[0]?.conversation?.conversation_group_id ?? undefined;
};

export const useIsSmsConversation = () => {
  const conversationId = useCurrentConversationId();
  const { data, isPending, isError } = useInfiniteMessages(conversationId);
  return {
    data:
      data === undefined
        ? undefined
        : data.pages[0]?.conversation?.title === "Text Follow-up",
    isPending,
    isError,
  };
};

export const useNoriConversationIdByCuraiId = (
  curaiConversationId: string | undefined,
) => {
  const { data: conversations } = useGetAllConversations();
  return conversations?.conversations.find(
    (convo) => convo.curai_conversation_id === curaiConversationId,
  )?.id;
};

export const useNoriConversationId = () => {
  const curaiConversationId = useCurrentConversationId();
  return useNoriConversationIdByCuraiId(curaiConversationId);
};

/**
 * Whether the Nori composer should be locked. Under `keep-conversations-open` a chat ends only when its
 * whole group is closed. A closed tail conversation is just the dormant state a user resumes from.
 * Off the flag this is the conversation's own closed state.
 */
export const useIsCurrentConversationClosed = () => {
  const useGroups = useKeepConversationsOpen();

  const conversationId = useCurrentConversationId();
  const { data } = useInfiniteMessages(conversationId);
  const groupId = data?.pages[0]?.conversation?.conversation_group_id;

  // Same query key as the stream's group read, so this is served from cache — no extra request.
  const { data: groupData } = useGroupMessages(groupId ?? undefined, {
    enabled: useGroups && Boolean(groupId),
  });

  if (useGroups) return groupData?.conversationGroup?.closed;
  return data?.pages[0]?.conversation?.closed;
};

/**
 * Whether a send should open a fresh conversation that joins the current group rather than post
 * into the one on screen.
 */
export const useJoinGroupOnSend = (conversationId?: string) => {
  const useGroups = useKeepConversationsOpen();
  const { data } = useInfiniteMessages(conversationId);
  const conversation = data?.pages[0]?.conversation;
  const groupId = conversation?.conversation_group_id;

  const { data: groupData } = useGroupMessages(groupId ?? undefined, {
    enabled: useGroups && Boolean(groupId),
  });

  const shouldStartNewConversation = Boolean(
    useGroups &&
      groupId &&
      conversation?.closed &&
      !groupData?.conversationGroup?.closed,
  );

  return {
    shouldStartNewConversation,
    conversationGroupId: shouldStartNewConversation ? groupId : null,
  };
};

/**
 * The NORI conversation a group's url should settle on: the group's newest one.
 *
 * Under `keep-conversations-open` the view renders a group as one stream whose live head is the
 * conversation named in the url, with the rest of the group appended above it as strictly older
 * history. A url naming an older sibling breaks that ordering — the newer conversation renders
 * above the older one and the view scrolls to the wrong end — and a send would compound it,
 * because the older conversation is closed so `useJoinGroupOnSend` opens a third conversation
 * instead of posting into the group's open one.
 *
 * Returns undefined when there is nothing to move to: flag off, group not resolved yet, a visit
 * rather than a NORI conversation, or the url already names the head.
 */
export const useGroupHeadConversationId = (conversationId?: string) => {
  const useGroups = useKeepConversationsOpen();
  const { data } = useInfiniteMessages(conversationId);
  const conversation = data?.pages[0]?.conversation;
  const groupId = conversation?.conversation_group_id;

  // Same query key as the stream's group read, so this is served from cache — no extra request.
  const { data: groupData } = useGroupMessages(groupId ?? undefined, {
    enabled: useGroups && Boolean(groupId),
  });

  // `groupId` is checked rather than inferred from the disabled query: a conversation that
  // predates groups has none, and it must never be redirected no matter what the group query holds.
  if (!useGroups || !groupId || conversation?.type !== "nori") return undefined;

  // Only move off a closed older sibling
  if (!conversation.closed) return undefined;

  const headId = groupData?.conversationGroup?.latest_nori_conversation_id;
  return headId && headId !== conversationId ? headId : undefined;
};

export const useIsConversationClosed = (conversationId: string | undefined) => {
  const { data } = useInfiniteMessages(conversationId);
  return data?.pages[0]?.conversation?.closed ?? false;
};

export const useNoriConversationCount = () => {
  const { data: conversations } = useGetAllConversations();
  return (
    conversations?.conversations.filter((convo) => convo.type === "nori")
      ?.length ?? 0
  );
};

export const useCurrentConversationWorkflowType = () => {
  const conversationId = useCurrentConversationId();
  const { data, isPending, isError } = useInfiniteMessages(conversationId);
  return {
    data: data?.pages[0]?.conversation?.initial_workflow_type,
    isPending,
    isError,
  };
};

export const useHasCuraiRedirect = () => {
  const conversationId = useCurrentConversationId();
  const {
    data: messagesData,
    isPending,
    isError,
  } = useInfiniteMessages(conversationId);
  return {
    // A VISIT-type CLINIC_REQUEST represents a clinic redirect (legacy CURAI_REDIRECT
    // events were backfilled to one), used here to hide the talk-to-doctor button.
    data: messagesData?.allMessages?.some(
      (message) =>
        message.type === AssistantMessageTypes.CLINIC_REQUEST &&
        (message.content as { type?: string } | undefined)?.type === "VISIT",
    ),
    isPending,
    isError,
  };
};

export const useHideTalkToDoctorButton = () => {
  const conversationId = useCurrentConversationId();
  const {
    data: conversationType,
    isPending: conversationTypePending,
    isError: conversationTypeError,
  } = useCurrentConversationType();
  const {
    data: hasCuraiRedirect,
    isPending: curaiRedirectPending,
    isError: curaiRedirectError,
  } = useHasCuraiRedirect();
  const {
    data: workflowType,
    isPending: workflowTypePending,
    isError: workflowTypeError,
  } = useCurrentConversationWorkflowType();
  const {
    data: isSmsConversation,
    isPending: isSmsConversationPending,
    isError: isSmsConversationError,
  } = useIsSmsConversation();
  const { value: isTalkToDoctorButtonEnabled } = useFeatureFlag(
    "talk-to-doctor-button-enabled",
    true,
  );

  const isPending =
    conversationTypePending ||
    curaiRedirectPending ||
    workflowTypePending ||
    isSmsConversationPending;
  const isError =
    conversationTypeError ||
    curaiRedirectError ||
    workflowTypeError ||
    isSmsConversationError;

  return (
    !isTalkToDoctorButtonEnabled ||
    !conversationId ||
    isPending ||
    isError ||
    hasCuraiRedirect ||
    conversationType === "curai" ||
    isSmsConversation ||
    workflowType === "diagnosis" ||
    workflowType === "rx_renewal"
  );
};
